import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, TargetIcon, ShieldIcon, CrosshairIcon, GhostIcon } from 'lucide-react';
interface Player {
  gamertag: string;
  realName: string;
  role: string;
  roleColor: string;
  rating: number;
  stats: {
    kd: number;
    winRate: number;
    clutch: number;
  };
  gradientFrom: string;
  gradientTo: string;
  icon: React.ElementType;
}
export function RosterCards() {
  const players: Player[] = [{
    gamertag: 'Vortex',
    realName: 'Kael Draven',
    role: 'IGL / Strategist',
    roleColor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    rating: 9.2,
    stats: {
      kd: 1.42,
      winRate: 68,
      clutch: 24
    },
    gradientFrom: 'from-cyan-600',
    gradientTo: 'to-blue-900',
    icon: TargetIcon
  }, {
    gamertag: 'Nyx',
    realName: 'Sable Orin',
    role: 'Entry Fragger',
    roleColor: 'bg-red-500/20 text-red-400 border-red-500/30',
    rating: 8.7,
    stats: {
      kd: 1.38,
      winRate: 62,
      clutch: 18
    },
    gradientFrom: 'from-red-600',
    gradientTo: 'to-orange-900',
    icon: CrosshairIcon
  }, {
    gamertag: 'Flux',
    realName: 'Theron Maren',
    role: 'Support',
    roleColor: 'bg-lime-500/20 text-lime-400 border-lime-500/30',
    rating: 8.4,
    stats: {
      kd: 1.15,
      winRate: 71,
      clutch: 12
    },
    gradientFrom: 'from-lime-600',
    gradientTo: 'to-green-900',
    icon: ShieldIcon
  }, {
    gamertag: 'Ash',
    realName: 'Zephyr Kova',
    role: 'AWPer',
    roleColor: 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30',
    rating: 9.0,
    stats: {
      kd: 1.56,
      winRate: 65,
      clutch: 31
    },
    gradientFrom: 'from-fuchsia-600',
    gradientTo: 'to-purple-900',
    icon: TargetIcon
  }, {
    gamertag: 'Phantom',
    realName: 'Liora Senn',
    role: 'Lurker',
    roleColor: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    rating: 8.9,
    stats: {
      kd: 1.33,
      winRate: 64,
      clutch: 27
    },
    gradientFrom: 'from-violet-600',
    gradientTo: 'to-indigo-900',
    icon: GhostIcon
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  return <section id="roster" className="relative py-24 sm:py-32 overflow-hidden" data-id="element-193">
      <div className="absolute inset-0 bg-gray-950" data-id="element-194" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-195">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-196">
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-white mb-4" data-id="element-197">
            YOUR LEGENDS. YOUR{' '}
            <span className="text-lime-400 text-glow-lime" data-id="element-198">LINEUP.</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-199">
            Showcase your roster with dynamic player cards built for the
            spotlight.
          </p>
        </motion.div>

        {/* Player Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-200">
          {players.map(player => <motion.div key={player.gamertag} variants={cardVariants} whileHover={{
          scale: 1.03,
          y: -5
        }} className="group relative bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden card-glow cursor-pointer" data-id="element-201">
              {/* Player Portrait Area (Gradient Placeholder) */}
              <div className={`relative h-40 bg-gradient-to-br ${player.gradientFrom} ${player.gradientTo}`} data-id="element-202">
                {/* Geometric pattern overlay */}
                <div className="absolute inset-0 opacity-30" data-id="element-203">
                  <div className="absolute top-4 right-4 w-16 h-16 border border-white/20 rotate-45" data-id="element-204" />
                  <div className="absolute bottom-4 left-4 w-8 h-8 border border-white/20" data-id="element-205" />
                </div>

                {/* Icon */}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-206">
                  <player.icon className="w-16 h-16 text-white/30" data-id="element-207" />
                </div>

                {/* Rating Badge */}
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-full" data-id="element-208">
                  <StarIcon className="w-3 h-3 text-yellow-400 fill-yellow-400" data-id="element-209" />
                  <span className="text-xs font-bold text-white" data-id="element-210">
                    {player.rating}
                  </span>
                </div>
              </div>

              {/* Angled clip overlay */}
              <div className="absolute top-36 left-0 right-0 h-8 bg-gray-900/80 clip-angled-top" data-id="element-211" />

              {/* Player Info */}
              <div className="relative p-4 pt-2" data-id="element-212">
                {/* Gamertag */}
                <h3 className="font-display font-bold text-xl text-white uppercase tracking-wide mb-0.5" data-id="element-213">
                  {player.gamertag}
                </h3>

                {/* Real Name */}
                <p className="text-sm text-gray-500 mb-3" data-id="element-214">{player.realName}</p>

                {/* Role Badge */}
                <span className={`inline-block px-2 py-1 text-xs font-bold uppercase tracking-wider rounded border ${player.roleColor} mb-4`} data-id="element-215">
                  {player.role}
                </span>

                {/* Stats */}
                <div className="space-y-2" data-id="element-216">
                  <div data-id="element-217">
                    <div className="flex justify-between text-xs mb-1" data-id="element-218">
                      <span className="text-gray-500" data-id="element-219">K/D</span>
                      <span className="text-white font-medium" data-id="element-220">
                        {player.stats.kd}
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden" data-id="element-221">
                      <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full" style={{
                    width: `${player.stats.kd / 2 * 100}%`
                  }} data-id="element-222" />
                    </div>
                  </div>

                  <div data-id="element-223">
                    <div className="flex justify-between text-xs mb-1" data-id="element-224">
                      <span className="text-gray-500" data-id="element-225">Win%</span>
                      <span className="text-white font-medium" data-id="element-226">
                        {player.stats.winRate}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden" data-id="element-227">
                      <div className="h-full bg-gradient-to-r from-lime-500 to-lime-400 rounded-full" style={{
                    width: `${player.stats.winRate}%`
                  }} data-id="element-228" />
                    </div>
                  </div>

                  <div data-id="element-229">
                    <div className="flex justify-between text-xs mb-1" data-id="element-230">
                      <span className="text-gray-500" data-id="element-231">Clutch</span>
                      <span className="text-white font-medium" data-id="element-232">
                        {player.stats.clutch}
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden" data-id="element-233">
                      <div className="h-full bg-gradient-to-r from-fuchsia-500 to-fuchsia-400 rounded-full" style={{
                    width: `${player.stats.clutch / 40 * 100}%`
                  }} data-id="element-234" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Hover glow effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-235">
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/10 to-transparent" data-id="element-236" />
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}